import { useState, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { DeviceStatus } from '@/components/device/DeviceStatus'
import { useDevice, useArchiveDevice, useRestoreDevice } from '@/hooks/useDevices'
import { useMeasurements } from '@/hooks/useMeasurements'
import { ArrowLeft, Edit, Battery, MapPin, Wifi, Calendar, History, Archive, RotateCcw, Upload, Camera, Loader2, Satellite } from 'lucide-react'
import { subDays, subHours, differenceInDays, format } from 'date-fns'
import { formatUTCToLocal } from '@/utils/date'
import { useToast } from '@/components/ui/use-toast'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { WatersenseView } from '@/components/device/views/WatersenseView'
import { RK900View } from '@/components/device/views/RK900View'
import { DHT11View } from '@/components/device/views/DHT11View'
import { DeviceMap } from '@/components/device/DeviceMap'
import { DeviceDebug } from '@/components/device/DeviceDebug'
import { DeviceActivityLog } from '@/components/device/DeviceActivityLog'
import { DeviceAlerts } from '@/components/device/DeviceAlerts'
import { OrbcommPanel } from '@/components/device/OrbcommPanel'
import { DataExportDialog } from '@/components/device/DataExportDialog'
import { Input } from '@/components/ui/input'
import { deviceService } from '@/services/device.service'
import type { WaterSenseMeasurement, Rk900Measurement, Dht11SoilMeasurement } from '@/types/measurement.types'

type TimePeriod = '24h' | '72h' | '7d' | '14d' | '30d' | '90d' | 'custom'

export const DeviceDetailPage = () => {
  const { deviceId } = useParams<{ deviceId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('7d')

  const archiveDevice = useArchiveDevice()
  const restoreDevice = useRestoreDevice()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const handleArchive = async () => {
    try {
      await archiveDevice.mutateAsync(deviceId!)
      toast({
        title: 'Success',
        description: 'Device archived successfully'
      })
      navigate('/devices')
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to archive device',
        variant: 'destructive'
      })
    }
  }

  const handleRestore = async () => {
    try {
      await restoreDevice.mutateAsync(deviceId!)
      toast({
        title: 'Success',
        description: 'Device restored successfully'
      })
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to restore device',
        variant: 'destructive'
      })
    }
  }
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image under 5MB.',
        variant: 'destructive'
      })
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setUploadingPhoto(true)
    try {
      await deviceService.uploadInstallPhoto(deviceId!, file)
      toast({
        title: 'Success',
        description: 'Install photo uploaded successfully.'
      })
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to upload install photo.',
        variant: 'destructive'
      })
    } finally {
      setUploadingPhoto(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const [customStartDate, setCustomStartDate] = useState<Date | null>(null)
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null)
  const [customDateError, setCustomDateError] = useState<string | null>(null)

  // Calculate measurement filters based on selected time period
  const measurementFilters = useMemo(() => {
    let endDate = new Date()
    let startDate: Date
    let limit: number

    switch (timePeriod) {
      case '24h':
        startDate = subHours(endDate, 24)
        limit = 500
        break
      case '72h':
        startDate = subHours(endDate, 72)
        limit = 500
        break
      case '7d':
        startDate = subDays(endDate, 7)
        limit = 500
        break
      case '14d':
        startDate = subDays(endDate, 14)
        limit = 1000
        break
      case '30d':
        startDate = subDays(endDate, 30)
        limit = 1000
        break
      case '90d':
        startDate = subDays(endDate, 90)
        limit = 2000
        break
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = customStartDate
          endDate = customEndDate
          limit = 5000
        } else {
          // Fall back to 7d while custom dates aren't set yet
          startDate = subDays(endDate, 7)
          limit = 500
        }
        break
      default:
        startDate = subDays(endDate, 7)
        limit = 500
    }

    const filters = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      limit
    }

    return filters
  }, [timePeriod, customStartDate, customEndDate])

  const { data: device, isLoading, error } = useDevice(deviceId!)
  const { data: measurements, error: measurementsError, isLoading: measurementsLoading } = useMeasurements(deviceId!, measurementFilters)

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !device) {
    return <ErrorMessage message="Failed to load device details. Please try again." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/devices">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold break-all">{device.name}</h1>
            <DeviceStatus status={device.status} />
          </div>
          <p className="text-sm text-muted-foreground ml-10 sm:ml-12 break-all">
            {device.serialNumber} • {device.accountName}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
          <DataExportDialog device={device} />
          <Button variant="outline" asChild>
            <Link to={`/devices/${deviceId}/history`}>
              <History className="mr-2 h-4 w-4" />
              Full History
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to={`/devices/${deviceId}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          {device.status === 'DECOMMISSIONED' ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restore
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Restore device?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will restore <strong>{device.name}</strong> from archived status and make it active again.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRestore} disabled={restoreDevice.isPending}>
                    {restoreDevice.isPending ? 'Restoring...' : 'Restore Device'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-orange-600 hover:text-orange-700">
                  <Archive className="mr-2 h-4 w-4" />
                  Archive
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive device?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will archive <strong>{device.name}</strong> and set its status to decommissioned. You can restore it later.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleArchive} disabled={archiveDevice.isPending} className="bg-orange-600 hover:bg-orange-700">
                    {archiveDevice.isPending ? 'Archiving...' : 'Archive Device'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Battery Level</CardTitle>
            <Battery className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {device.batteryLevel ? `${device.batteryLevel}%` : 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Seen</CardTitle>
            <Wifi className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-base font-medium">
              {device.lastSeen
                ? formatUTCToLocal(device.lastSeen)
                : 'Never'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Location</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {device.lat && device.long ? (
                <a
                  href={`https://www.google.com/maps?q=${device.lat},${device.long}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  {Number(device.lat).toFixed(4)}, {Number(device.long).toFixed(4)}
                </a>
              ) : 'Not set'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time Period Selector */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium">Chart Time Period</span>
            </div>
            <Select value={timePeriod} onValueChange={(value) => {
              setTimePeriod(value as TimePeriod)
              if (value !== 'custom') {
                setCustomDateError(null)
              }
            }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Last 24 Hours</SelectItem>
                <SelectItem value="72h">Last 72 Hours</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="14d">Last 14 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
                <SelectItem value="90d">Last 90 Days</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {timePeriod === 'custom' && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pl-7">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground whitespace-nowrap">Start</label>
                <Input
                  type="date"
                  className="w-[160px]"
                  value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                  max={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const val = e.target.value
                    if (!val) { setCustomStartDate(null); setCustomDateError(null); return }
                    const date = new Date(val + 'T00:00:00')
                    setCustomStartDate(date)
                    if (customEndDate) {
                      const days = differenceInDays(customEndDate, date)
                      if (days > 365) {
                        setCustomDateError('Range cannot exceed 365 days')
                      } else if (days < 0) {
                        setCustomDateError('Start date must be before end date')
                      } else {
                        setCustomDateError(null)
                      }
                    }
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground whitespace-nowrap">End</label>
                <Input
                  type="date"
                  className="w-[160px]"
                  value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                  max={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const val = e.target.value
                    if (!val) { setCustomEndDate(null); setCustomDateError(null); return }
                    // Set end date to end of day so the full day is included
                    const date = new Date(val + 'T23:59:59')
                    setCustomEndDate(date)
                    if (customStartDate) {
                      const days = differenceInDays(date, customStartDate)
                      if (days > 365) {
                        setCustomDateError('Range cannot exceed 365 days')
                      } else if (days < 0) {
                        setCustomDateError('Start date must be before end date')
                      } else {
                        setCustomDateError(null)
                      }
                    }
                  }}
                />
              </div>
              {customDateError && (
                <p className="text-sm text-destructive">{customDateError}</p>
              )}
              {!customStartDate || !customEndDate ? (
                <p className="text-sm text-muted-foreground">Select both dates to load data</p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="map">Map</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="debug">Debug</TabsTrigger>
          {device.orbcommModem && (
            <TabsTrigger value="orbcomm">
              <Satellite className="mr-2 h-4 w-4" />
              Orbcomm
            </TabsTrigger>
          )}
          <TabsTrigger value="activity">Activity Log</TabsTrigger>
          <TabsTrigger value="info">Information</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Render device-specific view based on device type */}
          {(() => {
            if (measurementsError) {
              return (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-sm text-destructive text-center">
                      Failed to load measurements. Please try refreshing.
                    </p>
                  </CardContent>
                </Card>
              )
            }

            if (measurementsLoading) {
              return <LoadingSpinner />
            }

            if (!measurements || !measurements.measurements.length) {
              return (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-sm text-muted-foreground text-center">
                      No measurements available for this device
                    </p>
                  </CardContent>
                </Card>
              )
            }

            const deviceType = device.deviceType?.toLowerCase() || ''

            // Check measurement structure to determine device type if deviceType string fails
            const firstMeasurement = measurements.measurements[0]
            const hasWaterSenseFields = 'liquidLevel' in firstMeasurement
            const hasRK900Fields = 'windSpeed' in firstMeasurement || 'pressure' in firstMeasurement
            const hasDHT11Fields = ('soilTemperature' in firstMeasurement || 'temperature' in firstMeasurement) &&
                                   ('soilMoisture' in firstMeasurement || 'soilmoistureLevel' in firstMeasurement)

            // Determine device type and render appropriate view
            if (deviceType.includes('watersense') || deviceType.includes('water') || deviceType.includes('sense') || hasWaterSenseFields) {
              return <WatersenseView device={device} measurements={measurements.measurements as WaterSenseMeasurement[]} />
            } else if (deviceType.includes('rk900') || deviceType.includes('rk-900') || deviceType.includes('900') || hasRK900Fields) {
              return <RK900View device={device} measurements={measurements.measurements as Rk900Measurement[]} />
            } else if (deviceType.includes('dht11') || deviceType.includes('dht') || deviceType.includes('soil') || hasDHT11Fields) {
              return <DHT11View device={device} measurements={measurements.measurements as Dht11SoilMeasurement[]} />
            } else {
              // Fallback - show debug info
              return (
                <Card>
                  <CardContent className="py-8 space-y-4">
                    <p className="text-sm text-muted-foreground text-center">
                      Unable to determine device type for visualization
                    </p>
                    <div className="text-xs text-muted-foreground space-y-2">
                      <p>Device Type: {device.deviceType || 'undefined'}</p>
                      <p>Measurements: {measurements.measurements.length}</p>
                      <p>First Measurement Fields: {Object.keys(firstMeasurement).join(', ')}</p>
                    </div>
                  </CardContent>
                </Card>
              )
            }
          })()}
        </TabsContent>

        <TabsContent value="map" className="space-y-4">
          <DeviceMap device={device} />
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <DeviceAlerts device={device} />
        </TabsContent>

        <TabsContent value="debug" className="space-y-4">
          {measurementsError ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-sm text-destructive text-center">
                  Failed to load measurement data for diagnostics. Please try refreshing.
                </p>
              </CardContent>
            </Card>
          ) : measurementsLoading ? (
            <LoadingSpinner />
          ) : measurements && measurements.measurements.length > 0 ? (
            <DeviceDebug
              device={device}
              measurementsData={measurements}
            />
          ) : (
            <Card>
              <CardContent className="py-8">
                <p className="text-sm text-muted-foreground text-center">
                  No measurement data available for diagnostics
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {device.orbcommModem && (
          <TabsContent value="orbcomm" className="space-y-4">
            <OrbcommPanel device={device} />
          </TabsContent>
        )}

        <TabsContent value="activity" className="space-y-4">
          <DeviceActivityLog device={device} />
        </TabsContent>

        <TabsContent value="info" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Install Photo</CardTitle>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
              {device.installPic ? (
                <div className="space-y-3">
                  <img
                    src={device.installPic}
                    alt="Device installation"
                    className="max-w-sm rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => fileInputRef.current?.click()}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={uploadingPhoto}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadingPhoto ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</>
                    ) : (
                      <><Upload className="mr-2 h-4 w-4" />Replace Photo</>
                    )}
                  </Button>
                </div>
              ) : (
                <div
                  className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 max-w-sm cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                  onClick={() => !uploadingPhoto && fileInputRef.current?.click()}
                >
                  {uploadingPhoto ? (
                    <>
                      <Loader2 className="h-10 w-10 text-muted-foreground mb-2 animate-spin" />
                      <p className="text-sm text-muted-foreground">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Camera className="h-10 w-10 text-muted-foreground mb-2" />
                      <p className="text-sm font-medium">Upload Install Photo</p>
                      <p className="text-xs text-muted-foreground mt-1">Click to select an image (max 5MB)</p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Device Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Device ID</p>
                  <p className="text-sm">{device.id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Serial Number</p>
                  <p className="text-sm">{device.serialNumber}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Account</p>
                  <p className="text-sm">{device.accountName}</p>
                </div>
                {device.modemId && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Modem ID</p>
                    <p className="text-sm">{device.modemId}</p>
                  </div>
                )}
                {device.hardwareVersion && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Hardware Version</p>
                    <p className="text-sm">{device.hardwareVersion}</p>
                  </div>
                )}
                {device.firmwareVersion && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Firmware Version</p>
                    <p className="text-sm">{device.firmwareVersion}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Created</p>
                  <p className="text-sm">
                    {device.createdAt ? formatUTCToLocal(device.createdAt, 'PPP') : 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Last Updated</p>
                  <p className="text-sm">
                    {device.updatedAt ? formatUTCToLocal(device.updatedAt, 'PPP') : 'Unknown'}
                  </p>
                </div>
              </div>
              {device.notes && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Notes</p>
                  <p className="text-sm mt-1">{device.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
